import OptimizationNewExpTreeDataSupplier from 'business/tree/OptimizationNewExpTreeDataSupplier';
import OptimizationTreeDataSupplier from 'business/tree/OptimizationTreeDataSupplier';
import { LabelRadioBox } from 'components/customComponents/Layers/InputGroups';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { OptimizationContext } from './OptimizationContext';
import { TreeNodeNameDTO } from 'business/models/Models';
import {
  getParentPath,
  getNewFolderType,
  getParents,
  isNullOrUndefined,
} from 'utils/Global';
import useRockFetchGet from 'utils/RockFetch';
import { OptimizationText, OptTextVariants } from './OptimizationComponents';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { EPlateState } from 'enums/EPlatestate';
import TreeNodeCreator from 'components/main/tree/CreateNodeModal';
import { ECreateNodeType } from 'components/main/tree/ProjectNode';
import WizardService from 'business/services/WizardService';
import RMSelect from 'components/customComponents/Controls/RMSelect';
import { useRMSelector } from 'redux/hooks';
import Explorer, { TreeControlDefaultState, TreeControlState } from 'components/explorer';
import { selectExp } from 'redux/selectors/ExperimentStateSelector';
import { SelectType } from 'business/models/CustomModels';
import { TreeService } from 'business/services/TreeService';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';
import { canAddProject } from 'components/explorer/utils';
import { FmlxBanner, FmlxButton, FmlxCheckBox } from 'fmlx-common-ui';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';

const treeService = new TreeService();
let isExperimentNameOverridden = false;

type OptimizationLocationProps = {
  totalNumOfWells: number;
};

export const OptimizationLocation = ({ totalNumOfWells }: OptimizationLocationProps) => {
  const [createNodeType, setCreateNodeType] = useState(ECreateNodeType.None);
  const context = useContext(OptimizationContext);
  const [treeNode, setTreeNode] = useState(undefined);
  const exp = useRMSelector(selectExp);
  const [plateTypes, setPlateTypes] = useState<SelectType[]>([]);
  const explorerRef = useRef(null);
  const [gSelectedNode, setgSelectedNode] = useState(null);
  const [treeControlState, setTreeControlState] = useState<TreeControlState>({
    ...TreeControlDefaultState,
    selectedNodes: [`node${gSelectedNode}`],
  });
  const [rockFetchGet] = useRockFetchGet();
  const [childTreeNodeNames, setChildTreeNodeNames] = useState<any[]>([]);
  const [validPlateTypeSelected, setValidPlateTypeSelected] = useState<boolean>(false);
  const [validTargetSelected, setValidTargetSelected] = useState<boolean>(true);
  const [validNameSelected, setValidNameSelected] = useState<boolean>(true);

  useEffect(() => {
    async function fetchPlates() {
      const service: WizardService = new WizardService();
      const response = await service.getPlateTypes();
      if (response != null) {
        let isValidPlateType = false;
        const plateTypeOptions = response
          .filter((pt) => pt.totalWells >= totalNumOfWells)
          .map((pt) => {
            isValidPlateType = pt.id == context.plateTypeId ? true : isValidPlateType;
            return { id: pt.id, name: pt.name };
          });
        setPlateTypes([{ id: 0, name: 'Select Plate Type' }, ...plateTypeOptions]);
        setValidPlateTypeSelected(isValidPlateType);
      }
    }
    fetchPlates();
    context.setExperimentName('');
    isExperimentNameOverridden = false;
  }, []);

  useEffect(() => {
    if (
      !isNullOrUndefined(treeNode) &&
      treeNode?.accessRightName === ETreeNodeAccessRightNames.Modify
    ) {
      if (treeNode.type == 'Experiment') {
        context.setExperimentId(treeNode.extra.id);
        setValidTargetSelected(true);
        context.setExperimentName(treeNode.name);
      } else if (
        context.isNewExperiment &&
        (treeNode.type == 'Project' || treeNode.type == 'ProjectFolder')
      ) {
        context.setExperimentId(treeNode.id);
        setValidTargetSelected(true);
        if (isExperimentNameOverridden === false) {
          setNewExperimentName();
        }
      } else {
        setValidTargetSelected(false);
      }
      if (
        treeNode.type == ETreeNodeTypes.Project ||
        treeNode.type == ETreeNodeTypes.ProjectFolder
      ) {
        const parents = getParents(treeControlState.treenodes, treeNode.id);
        const expUrl = getParentPath(parents);
        context.setExperimentUrl(expUrl);
      }
    } else {
      setValidTargetSelected(false);
    }

    async function setNewExperimentName() {
      const treeNodeName: TreeNodeNameDTO = await rockFetchGet(
        `/api/Tree/getnewname/${treeNode.id}/${ETreeNodeTypes.Experiment}`,
        'get',
        undefined,
        true,
      );
      context.setExperimentName(treeNodeName.name);
    }
  }, [treeNode, context.isNewExperiment]);

  useEffect(() => {
    context.setNextEnabled(
      validNameSelected &&
        validTargetSelected &&
        (!context.isNewExperiment || validPlateTypeSelected),
    );
  }, [validNameSelected, validPlateTypeSelected, validTargetSelected]);

  async function loadChildNodes(treeNodeId: number) {
    setChildTreeNodeNames([]);
    const controller = new AbortController();
    const signal = controller.signal;
    const childNodes = await treeService.getTreeNodesService(
      treeNodeId,
      [ETreeNodeTypes.Experiment],
      signal,
    );

    const names = childNodes.map((node) => node.name);
    setChildTreeNodeNames(names);

    setValidNameSelected(!names.includes(context.experimentName));
  }
  const [commonTreeNodes, setCommonTreeNodes] = useState([]);

  async function newNode(newNode, parentTreeNodeId) {
    await explorerRef.current.newNode(newNode, [], parentTreeNodeId);
  }
  function createFolderClicked(nodeType) {
    setCreateNodeType(nodeType);
  }
  function getCreateTreeNodeModel() {
    if (!isNullOrUndefined(treeNode)) {
      return (
        <TreeNodeCreator
          type={createNodeType}
          setType={setCreateNodeType}
          parentTreeNodeId={treeNode?.id}
          newNode={newNode}
          newFolderType={getNewFolderType(
            treeControlState.treenodes,
            treeControlState.selectedExplorerNode,
          )}
        />
      );
    }
  }

  const folderNodeTypes = [
    ETreeNodeTypes.Project,
    ETreeNodeTypes.ProjectFolder,
    ETreeNodeTypes.ProjectsFolder,
    ETreeNodeTypes.Projects,
    ETreeNodeTypes.Experiment,
  ];

  const shouldRenderNode = (node: any) => {
    if (!folderNodeTypes.includes(node.type) || node.type === ETreeNodeTypes.RecycleBin) {
      return;
    }

    if (node.type === ETreeNodeTypes.Experiment) {
      if (context.isNewExperiment || node.extra?.wellCount < totalNumOfWells) {
        return null;
      }

      const plateState = node.extra?.plateState;
      if (
        plateState === EPlateState.Dispensed ||
        plateState === EPlateState.Imaged ||
        plateState === EPlateState.CrystalHarvested ||
        plateState === EPlateState.Expired
      ) {
        return null;
      }
    }

    if (
      folderNodeTypes.includes(node.type) &&
      !node.children &&
      node.type !== ETreeNodeTypes.RecycleBin
    ) {
      return node;
    }

    const filteredData = node.children?.map((_node) => shouldRenderNode(_node)) || node;

    return {
      ...node,
      children: filteredData.filter(
        (x) => x && x !== null && x.extra?.id != context.sourceExperimentId,
      ),
    };
  };

  const handlePlateTypeChange = (e) => {
    const selectedPlateTypeId = parseInt(e.target.value);
    context.setPlateTypeId(selectedPlateTypeId);
    setValidPlateTypeSelected(selectedPlateTypeId > 0);
  };

  const isCreateProjectDisabled = !context.isNewExperiment || canAddProject(treeNode);

  return (
    <div id="optimizationlocation-wrapper" className="h-100 flex flex-wrap gap-2 p-4">
      <div
        id="optimizationlocation-leftpanel"
        className="basis-[37%] gap-2 h-fit p-2 border border-dashed border-black"
      >
        <OptimizationText
          text="New Experiment Option"
          variant={OptTextVariants.heading}
        />
        <div className="flex flex-wrap gap-2">
          <LabelRadioBox
            labelText={'Create New Experiment'}
            radioValue={context.isNewExperiment}
            onChange={() => context.setIsNewExperiment(true)}
            name="newExp"
            disabled={false}
          />
          <LabelRadioBox
            labelText={'Add a layer to an existing experiment'}
            radioValue={!context.isNewExperiment}
            onChange={() => context.setIsNewExperiment(false)}
            name="newExp"
            disabled={false}
          />
        </div>
        {context.isNewExperiment && (
          <>
            <div
              id="optimizationlocation-platetype-row"
              className="flex flex-wrap items-baseline m-2"
            >
              <label
                id="optimizationlocation-platetype-label"
                className="text-sm basis-2/5"
              >
                Select Plate Type
              </label>
              <RMSelect
                style={{ heigth: '2rem', flex: '60%' }}
                options={plateTypes}
                selectedValue={context.plateTypeId}
                onChange={handlePlateTypeChange}
                placeholder={'Select Plate Type'}
              />
            </div>
            <FmlxBanner
              variant="info"
              title="Unable to optimize on the selected plate due to incorrect well configuration"
              hideCloseButton
              show
            />
          </>
        )}
      </div>
      <div
        id="optimizationlocation-rightpanel"
        className="p-2 h-[36vh] flex-[62%] border border-dashed border-black"
      >
        <div
          id="optimizationlocation-definelocation-wrapper"
          className="flex justify-between items-center mb-2"
        >
          <OptimizationText
            text="Define Location"
            className="!pb-0"
            variant={OptTextVariants.heading}
          />
          <div id="optimizationlocation-button-wrapper" className="flex space-x-2">
            <FmlxButton
              id="optimizationlocation-newfolder-button"
              type="basic"
              label="New Folder"
              disabled={
                !context.isNewExperiment ||
                treeNode?.accessRightName !== ETreeNodeAccessRightNames.Modify
              }
              onClick={(e) => {
                createFolderClicked(ECreateNodeType.ProjectFolder);
              }}
            />
            <FmlxButton
              id="optimizationlocation-newproject-button"
              disabled={isCreateProjectDisabled}
              variant="outline"
              type="basic"
              onClick={(e) => {
                createFolderClicked(ECreateNodeType.Project);
              }}
              label="New Project"
            />
          </div>
        </div>
        <div id="optimizationlocation-explorer-container" style={{ height: '65%' }}>
          {context.isNewExperiment && (
            <Explorer
              ref={explorerRef}
              showQuickAccessNode={false}
              shouldRenderNode={shouldRenderNode}
              setParentNode={setTreeNode}
              dataSupplier={OptimizationNewExpTreeDataSupplier}
              setCommonTreeNodes={setCommonTreeNodes}
              experimentID={exp.expId || exp.experimentId}
              disableOpen
              isSetCommon={true}
              commonTreeNodes={commonTreeNodes}
              setTreeControlState={setTreeControlState}
              treeControlState={treeControlState}
              setgSelectedNode={setgSelectedNode}
              treeNodeIdToLoadTree={context.parentTreeNodeId}
              onNodeClickSelectPostAction={(node) => {
                loadChildNodes(node.id);
              }}
            />
          )}
          {!context.isNewExperiment && (
            <Explorer
              ref={explorerRef}
              showQuickAccessNode={false}
              shouldRenderNode={shouldRenderNode}
              setParentNode={setTreeNode}
              dataSupplier={OptimizationTreeDataSupplier}
              setCommonTreeNodes={setCommonTreeNodes}
              experimentID={exp.expId || exp.experimentId}
              disableOpen
              commonTreeNodes={commonTreeNodes}
              setTreeControlState={setTreeControlState}
              treeControlState={treeControlState}
              setgSelectedNode={setgSelectedNode}
              treeNodeIdToLoadTree={context.parentTreeNodeId}
              onNodeClickSelectPostAction={(node) => {
                loadChildNodes(node.id);
              }}
            />
          )}
        </div>
        <div
          id="optimizationlocation-expname-row"
          className="flex pt-2 align-baseline gap-2"
        >
          <label
            id="optimizationlocation-expname-label"
            className="flex self-center text-center"
            style={{ fontWeight: 400, fontSize: '13px' }}
          >
            Experiment Name
          </label>
          <div id="optimizationlocation-expname-input-wrapper">
            <input
              id="optimizationlocation-expname-input"
              type="text"
              style={{ width: '15rem', height: '2rem' }}
              onChange={(e) => {
                isExperimentNameOverridden = true;
                context.setExperimentName(e.target.value);
                if (childTreeNodeNames.includes(e.target.value)) {
                  setValidNameSelected(false);
                } else {
                  setValidNameSelected(true);
                }
              }}
              value={context.experimentName}
              disabled={!context.isNewExperiment}
            />
            {childTreeNodeNames.includes(context.experimentName) &&
              context.isNewExperiment && (
                <div
                  id="optimizationlocation-expname-exists-message-wrapper"
                  className="flex justify-center items-center absolute gap-2"
                >
                  <RMSvgIcon
                    id="optimizationlocation-expname-exists-message-icon"
                    fill="#DB0000"
                    width={12}
                    name="Error"
                  />
                  <div
                    id="optimizationlocation-expname-exists-message-text"
                    className=" text-[#DB0000] text-sm font-normal"
                  >
                    Experiment name already exists
                  </div>
                </div>
              )}
          </div>
          <FmlxCheckBox
            id="optimizationlocation-expname-openexperiment-checkbox"
            checked={context.openExperiment}
            onChange={() => {
              context.setOpenExperiment(!context.openExperiment);
            }}
            label="Open Experiment After Optimization"
          />
        </div>
      </div>
      {getCreateTreeNodeModel()}
    </div>
  );
};
